import { fromOpenAlgoInterval } from './openAlgoBars';

/** A saved visible window. Logical indexes, the pair `getVisibleLogicalRange` returns. */
export interface ZoomWindow {
  from: number;
  to: number;
  /** How many bars the window was measured on. The next symbol keeps the same right edge. */
  bars?: number;
}

/** Wash behind premarket and after-hours bars while the session is extended. */
export interface SessionShade {
  color: string;
  opacity: number;
}

/**
 * Desk chrome stored on `window.config.chart`.
 * Absent means the widget has never been saved, which is the first open.
 */
export interface SavedChart {
  interval: string;
  chartType: string;
  extendedHours: boolean;
  crosshair: boolean;
  openAlgo: unknown;
  drawings: Record<string, unknown>;
  zoom: Record<string, ZoomWindow>;
  sessionShade?: SessionShade;
}

const SHADE_COLOR = '#c47b2b';
const SHADE_OPACITY = 0.22;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** The library's own faint wash disappears on `#0A0A0A`. This orange reads on it. */
export function defaultSessionShade(): SessionShade {
  return { color: SHADE_COLOR, opacity: SHADE_OPACITY };
}

export function emptySavedChart(): SavedChart {
  return {
    interval: '1m',
    chartType: 'candlestick',
    extendedHours: false,
    crosshair: true,
    openAlgo: undefined,
    drawings: {},
    zoom: {},
  };
}

/** First open is the only time Zeroday Scalper is added. A later save that omits it stays omitted. */
export function shouldSeedIndicator(saved: SavedChart | undefined): boolean {
  return saved === undefined;
}

/** `D` and `1d` are the same timeframe. Zoom keys use the pane's code. */
export function canonicalIntervalCode(code: string): string {
  const parsed = fromOpenAlgoInterval(code);
  if (!parsed) return code;
  if (parsed.unit === 'month') return `${parsed.count}mo`;
  const suffix = { minute: 'm', hour: 'h', day: 'd', week: 'w' } as const;
  return `${parsed.count}${suffix[parsed.unit]}`;
}

export function sharedZoomKey(intervalCode: string): string {
  return `*|${canonicalIntervalCode(intervalCode)}`;
}

export function zoomKey(symbol: string, intervalCode: string): string {
  return `${symbol.trim().toUpperCase()}|${canonicalIntervalCode(intervalCode)}`;
}

export function zoomWindow(value: unknown): ZoomWindow | null {
  if (!isRecord(value)) return null;
  const { from, to, bars } = value;
  if (typeof from !== 'number' || typeof to !== 'number') return null;
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return null;
  if (typeof bars === 'number' && Number.isFinite(bars) && bars >= 1) return { from, to, bars };
  return { from, to };
}

export function readZoomMap(raw: unknown): Record<string, ZoomWindow> {
  if (!isRecord(raw)) return {};
  const out: Record<string, ZoomWindow> = {};
  for (const [key, value] of Object.entries(raw)) {
    const window = zoomWindow(value);
    if (window) out[key] = window;
  }
  return out;
}

/** The timeframe's window, or the older per-symbol window when that is all this chart has saved. */
export function lookupZoom(
  map: Record<string, ZoomWindow>,
  symbol: string,
  intervalCode: string,
): ZoomWindow | null {
  const code = canonicalIntervalCode(intervalCode);
  return map[sharedZoomKey(code)] ?? map[zoomKey(symbol, code)] ?? null;
}

/**
 * Sit `window` on a series of `barCount` bars.
 * The right edge stays the same number of bars past the last bar.
 */
export function placeZoom(window: ZoomWindow, barCount: number): ZoomWindow {
  const span = window.to - window.from;
  const basis = window.bars;
  if (!(barCount > 0) || !(span > 0) || basis == null || !(basis > 0)) {
    return { from: window.from, to: window.to };
  }
  const rightGap = window.to - (basis - 1);
  const to = barCount - 1 + rightGap;
  return { from: to - span, to };
}

export function readDrawingMap(raw: unknown): Record<string, unknown> {
  if (!isRecord(raw)) return {};
  return { ...raw };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** A color input can only show `#rrggbb`. Anything else is dropped. */
export function sanitizeSessionShade(raw: unknown): SessionShade | null {
  if (!isRecord(raw)) return null;
  if (typeof raw.color !== 'string' || !/^#[0-9a-fA-F]{6}$/.test(raw.color)) return null;
  if (typeof raw.opacity !== 'number' || !Number.isFinite(raw.opacity)) return null;
  return { color: raw.color.toLowerCase(), opacity: clamp(raw.opacity, 0, 1) };
}

/** `rgba()` for the pre/post wash. */
export function shadeCss(shade: SessionShade | null | undefined): string {
  const look = shade ?? defaultSessionShade();
  const raw = look.color.slice(1);
  const n = Number.parseInt(raw, 16);
  const red = (n >> 16) & 255;
  const green = (n >> 8) & 255;
  const blue = n & 255;
  return `rgba(${red}, ${green}, ${blue}, ${look.opacity})`;
}

/** Daily and higher stay on the regular session. Only intraday asks for pre and post bars. */
export function intervalAllowsExtended(intervalCode: string): boolean {
  const parsed = fromOpenAlgoInterval(intervalCode);
  if (!parsed) return false;
  return parsed.unit === 'minute' || parsed.unit === 'hour';
}

export function usesExtendedSession(extendedHours: boolean, intervalCode: string): boolean {
  return extendedHours && intervalAllowsExtended(intervalCode);
}

export function sessionFilterFor(extendedHours: boolean, intervalCode: string): 'rth' | 'all' {
  return usesExtendedSession(extendedHours, intervalCode) ? 'all' : 'rth';
}

/** Read a stored chart. Anything that is not an object is a first open. */
export function readSavedChart(raw: unknown): SavedChart | undefined {
  if (!isRecord(raw)) return undefined;
  const shade = sanitizeSessionShade(raw.sessionShade);
  const chart: SavedChart = {
    interval: typeof raw.interval === 'string' && raw.interval ? raw.interval : '1m',
    chartType: typeof raw.chartType === 'string' && raw.chartType ? raw.chartType : 'candlestick',
    extendedHours: raw.extendedHours === true,
    crosshair: raw.crosshair !== false,
    openAlgo: 'openAlgo' in raw ? raw.openAlgo : undefined,
    drawings: readDrawingMap(raw.drawings),
    zoom: readZoomMap(raw.zoom),
  };
  if (shade) chart.sessionShade = shade;
  return chart;
}

/**
 * The widget snapshot's viewport indexes whatever series was on screen.
 * Zoom is stored separately, per timeframe. The snapshot keeps indicators and panes.
 */
export function dropViewport(state: unknown): unknown {
  if (!isRecord(state)) return state;
  const chart = state.chart;
  if (!isRecord(chart) || !('viewport' in chart)) return state;
  const nextChart = { ...chart };
  delete nextChart.viewport;
  return { ...state, chart: nextChart };
}
